import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { createUseStyles } from "react-jss";
import type {
  BoatChecklist,
  ChecklistNotify,
  ChecklistPhase,
  SaveBoatChecklist,
} from "../../../../common/src";
import {
  checklistNotifySchema,
  checklistPhaseSchema,
  saveBoatChecklistSchema,
} from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import { toFieldErrors } from "../../utils/fieldErrors";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { EmptyState, LoadError, Loading } from "../shared/StatusViews";
import { useFormStyles } from "../shared/styles";

const PHASE_LABELS: Record<ChecklistPhase, string> = {
  pickup: "Pickup",
  return: "Return",
  both: "Both",
};

const NOTIFY_LABELS: Record<ChecklistNotify, string> = {
  never: "Never",
  problems: "Only when a problem is reported",
  always: "Every time a checklist is submitted",
};

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  list: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 },
  row: {
    display: "grid",
    gridTemplateColumns: "28px minmax(0, 1fr) 110px auto",
    gap: 8,
    alignItems: "center",
    [theme.narrow]: {
      gridTemplateColumns: "24px minmax(0, 1fr)",
      "& > :nth-child(n+3)": { gridColumn: "2" },
    },
  },
  number: { color: theme.color.muted, fontWeight: 700, textAlign: "right" },
  buttons: { display: "flex", gap: 4 },
}));

interface EditableItem {
  key: number;
  label: string;
  phase: ChecklistPhase;
}

let nextKey = 1;
const withKey = (item: {
  label: string;
  phase: ChecklistPhase;
}): EditableItem => ({
  key: nextKey++,
  ...item,
});

const ChecklistForm = ({
  boatId,
  initial,
}: {
  boatId: number;
  initial: BoatChecklist;
}) => {
  const classes = useStyles();
  const form = useFormStyles();
  const [items, setItems] = useState<EditableItem[]>(() =>
    initial.items.map(withKey)
  );
  const [notify, setNotify] = useState<ChecklistNotify>(initial.notify);
  const [notifyEmail, setNotifyEmail] = useState(initial.notifyEmail ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const save = useAction(
    useCallback(
      (input: SaveBoatChecklist) => trpc.boat.saveChecklist.mutate(input),
      []
    )
  );

  const change = (next: EditableItem[]) => {
    setSaved(false);
    setItems(next);
  };
  const update = (key: number, patch: Partial<EditableItem>) =>
    change(
      items.map((item) => (item.key === key ? { ...item, ...patch } : item))
    );
  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    const moved = items[index];
    const other = items[target];
    if (!moved || !other) {
      return;
    }
    const next = [...items];
    next[index] = other;
    next[target] = moved;
    change(next);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = saveBoatChecklistSchema.safeParse({
      boatId,
      items: items.map(({ label, phase }) => ({ label, phase })),
      notify,
      notifyEmail,
    });
    if (!parsed.success) {
      setErrors(toFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    if (await save.run(parsed.data)) {
      setSaved(true);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} noValidate className={form.stack}>
      <p className={form.intro}>
        Borrowers check these off when they pick up and return the boat. Most
        items apply to both; mark one Pickup or Return when it only applies then
        (e.g. “Remove hitch lock, code 1234”). Put quantities in the text, like
        “3 life vests aboard”.
      </p>
      {items.length === 0 ? (
        <EmptyState title="No checklist items yet">
          Add the first item below.
        </EmptyState>
      ) : (
        <ol className={classes.list}>
          {items.map((item, index) => (
            <li key={item.key} className={classes.row}>
              <span className={classes.number}>{index + 1}.</span>
              <input
                className={form.control}
                aria-label={`Item ${index + 1}`}
                value={item.label}
                maxLength={200}
                placeholder="e.g. 3 life vests aboard"
                onChange={(e) => update(item.key, { label: e.target.value })}
              />
              <select
                className={form.control}
                aria-label={`When item ${index + 1} applies`}
                value={item.phase}
                onChange={(e) => {
                  const phase = checklistPhaseSchema.safeParse(e.target.value);
                  if (phase.success) {
                    update(item.key, { phase: phase.data });
                  }
                }}
              >
                {checklistPhaseSchema.options.map((phase) => (
                  <option key={phase} value={phase}>
                    {PHASE_LABELS[phase]}
                  </option>
                ))}
              </select>
              <span className={classes.buttons}>
                <Button
                  variant="ghost"
                  small
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label="Move up"
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  small
                  disabled={index === items.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label="Move down"
                >
                  ↓
                </Button>
                <Button
                  variant="ghost"
                  small
                  onClick={() =>
                    change(items.filter((i) => i.key !== item.key))
                  }
                  aria-label={`Remove item ${index + 1}`}
                >
                  ✕
                </Button>
              </span>
            </li>
          ))}
        </ol>
      )}
      {errors.items && <Notice kind="error">{errors.items}</Notice>}
      <div>
        <Button
          variant="secondary"
          small
          onClick={() =>
            change([...items, withKey({ label: "", phase: "both" })])
          }
        >
          + Add item
        </Button>
      </div>
      <div className={form.grid}>
        <Field
          label="Email me about submitted checklists"
          htmlFor="checklist-notify"
        >
          <select
            id="checklist-notify"
            className={form.control}
            value={notify}
            onChange={(e) => {
              const parsed = checklistNotifySchema.safeParse(e.target.value);
              if (parsed.success) {
                setSaved(false);
                setNotify(parsed.data);
              }
            }}
          >
            {checklistNotifySchema.options.map((option) => (
              <option key={option} value={option}>
                {NOTIFY_LABELS[option]}
              </option>
            ))}
          </select>
        </Field>
        {notify !== "never" && (
          <Field
            label="Send them to"
            htmlFor="checklist-email"
            error={errors.notifyEmail}
          >
            <input
              id="checklist-email"
              type="email"
              className={form.control}
              value={notifyEmail}
              onChange={(e) => {
                setSaved(false);
                setNotifyEmail(e.target.value);
              }}
            />
          </Field>
        )}
      </div>
      {save.error && <Notice kind="error">{save.error}</Notice>}
      {saved && <Notice kind="success">Checklist saved.</Notice>}
      <div className={form.actions}>
        <Button type="submit" disabled={save.pending}>
          {save.pending ? "Saving…" : "Save checklist"}
        </Button>
      </div>
    </form>
  );
};

/** The owner's pickup and return checklist for one boat. */
export const ChecklistEditor = ({ boatId }: { boatId: number }) => {
  const load = useCallback(
    (signal: AbortSignal) => trpc.boat.checklist.query({ boatId }, { signal }),
    [boatId]
  );
  const checklist = useAsyncData(load);
  return (
    <Card title="Pickup & return checklist">
      {checklist.error && (
        <LoadError message={checklist.error} onRetry={checklist.reload} />
      )}
      {checklist.loading && !checklist.data && <Loading />}
      {checklist.data && (
        <ChecklistForm boatId={boatId} initial={checklist.data} />
      )}
    </Card>
  );
};
